import { DeviceTypes } from './../../types/state/metaTypes'
import { endPoints } from './../../constants/index'
import { APIMethods, ReqBodyWithHeaders } from './../../types/state/apiTypes'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchApi } from '../../helpers/Api'
import { setErrorStatus, setLoadingStatus, updateView } from './meta'
import { ViewTypes } from '../../types/state'
import { OptionTypes } from '../../types/components'

interface VotesType {
	candidates: CandidateItemType[]
	startTime: number
	endTime: number
	alreadyParticipated: boolean
	questionData: QuestionType
}

export interface QuestionType {
	active: boolean
	id: string
	multiSelect: boolean
	options: OptionTypes[]
	platformName: string
	alternateQuestionText: string
	question: string
	timelineId: string
}
export interface CandidateItemType {
	id: string
	name: string
	imageURL: string
	contestantId: number
	active: boolean
	state: string
	city: string
	age: number
	selected: boolean
}

const initialState: VotesType = {
	candidates: [],
	startTime: 0,
	endTime: 0,
	alreadyParticipated: true,
	questionData: {
		active: true,
		id: '',
		multiSelect: false,
		options: [],
		platformName: '',
		alternateQuestionText: '',
		question: '',
		timelineId: '',
	},
}

export const saveVotes = createAsyncThunk(
	'candidates/vote',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response.status === 200) {
			thunkAPI.dispatch(updateView(ViewTypes.OFFERS))
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(setErrorStatus({ status: false, message: '' }))
		} else {
			thunkAPI.dispatch(updateView(ViewTypes.LOGIN))
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Failed to submit votes. Please try again',
				})
			)
		}
	}
)

export const saveQuizAnswer = createAsyncThunk(
	'khbc/answer',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response.status === 200) {
			thunkAPI.dispatch(updateView(ViewTypes.OFFERS))
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(setErrorStatus({ status: false, message: '' }))
		} else {
			thunkAPI.dispatch(updateView(ViewTypes.LOGIN))
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Failed to submit votes. Please try again',
				})
			)
		}
	}
)

export const getUserSubmission = createAsyncThunk(
	'khbc/checkSubmissonExist',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response.status === 200) {
			if (response.result) {
				thunkAPI.dispatch(updateView(ViewTypes.OFFERS))
			} else {
				thunkAPI.dispatch(updateParticipationStatus(response.result))
			}
			thunkAPI.dispatch(setLoadingStatus(false))
		} else {
			thunkAPI.dispatch(updateView(ViewTypes.LOGIN))
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({
					status: true,
					message: 'Failed to submit votes. Please try again',
				})
			)
		}
	}
)

export const getCandidates = createAsyncThunk(
	'candidates/active',
	async (reqObj: { client: DeviceTypes; sessionId: string }, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const Obj: ReqBodyWithHeaders = {
			method: APIMethods.GET,
			url: endPoints.contestants,
			client: reqObj.client,
			sessionId: reqObj.sessionId,
		}
		const response = await fetchApi(Obj, true)
		if (response.length > 0) {
			const updatedRes: CandidateItemType[] = []
			response.forEach((obj: any) => {
				const candidate: CandidateItemType = {
					id: obj.id,
					age: obj.age,
					active: obj.active,
					city: obj.city,
					contestantId: +obj.contestantId,
					imageURL: obj.imageUrl,
					name: obj.name,
					state: obj.state,
					selected: false,
				}
				updatedRes.push(candidate)
			})

			thunkAPI.dispatch(
				getTimeLine({ candidates: updatedRes, sessionId: reqObj.sessionId })
			)
		} else {
			thunkAPI.dispatch(
				getTimeLine({ candidates: [], sessionId: reqObj.sessionId })
			)
		}
	}
)

export const getQuestions = createAsyncThunk(
	'khbc/getQuestions',
	async (reqObj: ReqBodyWithHeaders, thunkAPI) => {
		thunkAPI.dispatch(setLoadingStatus(true))
		const response = await fetchApi(reqObj, true)
		if (response[0].id) {
			thunkAPI.dispatch(setLoadingStatus(false))
			return {
				questions: response[0],
			}
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			return {
				questions: [],
			}
		}
	}
)

const getTimeLine = createAsyncThunk(
	'getVotes/timeline',
	async (
		reqObj: { candidates: CandidateItemType[]; sessionId: string },
		thunkAPI
	) => {
		const obj: ReqBodyWithHeaders = {
			url: endPoints.timeline,
			method: APIMethods.GET,
			client: 'desktop',
			sessionId: reqObj.sessionId,
		}
		const response = await fetchApi(obj, true)
		if (response.id) {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(setErrorStatus({ status: false, message: '' }))
			return {
				candidates: reqObj.candidates,
				stateDate: response.startDateTime,
				endDate: response.endDateTime,
			}
		} else {
			thunkAPI.dispatch(setLoadingStatus(false))
			thunkAPI.dispatch(
				setErrorStatus({ status: true, message: 'Failed to fetch data' })
			)
			return {
				candidates: reqObj.candidates,
				stateDate: 0,
				endDate: 0,
			}
		}
	}
)

export const votesSlice = createSlice({
	name: 'votes',
	initialState,
	reducers: {
		updateStatus: (
			state,
			action: PayloadAction<{ id: string; candidateId: number }>
		) => {
			const candidate = state.candidates.filter(
				(ele) => ele.id === action.payload.id
			)
			candidate[0].selected = !candidate[0].selected
		},
		updateParticipationStatus: (state, action: PayloadAction<boolean>) => {
			state.alreadyParticipated = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getTimeLine.fulfilled, (state, action) => {
			state.endTime = action.payload?.endDate
			state.startTime = action.payload?.stateDate
			state.candidates = action.payload.candidates
		})
		builder.addCase(getQuestions.fulfilled, (state, action) => {
			state.questionData = action.payload?.questions
		})
	},
})

export const { updateStatus, updateParticipationStatus } = votesSlice.actions
export default votesSlice.reducer
